import React, { useEffect, ReactNode, useState } from "react";

interface GenericModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  onDecline?: () => void;
  children: ReactNode;
  acceptText?: string;
  cancelText?: string;
  header?: string;
  customCss?: React.CSSProperties;
  customWidth?: string; // Control modal width
  customHeight?: string; // Control modal height
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full'; // Predefined sizes
  padding?: string; // Custom padding
  hideCloseButton?: boolean; // Option to hide the close button
  contentPadding?: string; // Custom padding for content area
  closeOnBackdropClick?: boolean; // Option to close on backdrop click
  showAnimation?: boolean; // Control animation
  variant?: 'primary' | 'danger' | 'success' | 'warning' | 'info'; // Button color variants
}

const GenericModal = ({
  isOpen,
  onClose,
  onAccept,
  onDecline,
  children,
  acceptText = "Accept",
  cancelText = "Cancel",
  header,
  customCss,
  customWidth,
  customHeight,
  size = "md",
  padding,
  hideCloseButton = false,
  contentPadding,
  closeOnBackdropClick = true,
  showAnimation = true,
  variant = "primary",
}: GenericModalProps) => {
  const [isClosing, setIsClosing] = useState(false);

  // Handle controlled closing animation using useCallback to avoid dependency issues
  const handleCloseWithAnimation = React.useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  }, [onClose]);

  const handleCloseWithoutAnimation = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleClose = showAnimation ? handleCloseWithAnimation : handleCloseWithoutAnimation;
  // Size classes mapping
  const sizeClasses = {
    xs: "max-w-xs",
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-full mx-4"
  };

  // Variant classes for buttons
  const variantClasses = {
    primary: {
      accept: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-300 text-white",
      decline: "border border-gray-200 text-gray-500 hover:bg-gray-100 focus:ring-gray-200"
    },
    danger: {
      accept: "bg-red-600 hover:bg-red-700 focus:ring-red-300 text-white",
      decline: "border border-gray-200 text-gray-500 hover:bg-gray-100 focus:ring-gray-200"
    },
    success: {
      accept: "bg-green-600 hover:bg-green-700 focus:ring-green-300 text-white",
      decline: "border border-gray-200 text-gray-500 hover:bg-gray-100 focus:ring-gray-200"
    },
    warning: {
      accept: "bg-amber-500 hover:bg-amber-600 focus:ring-amber-300 text-white",
      decline: "border border-gray-200 text-gray-500 hover:bg-gray-100 focus:ring-gray-200"
    },
    info: {
      accept: "bg-cyan-600 hover:bg-cyan-700 focus:ring-cyan-300 text-white",
      decline: "border border-gray-200 text-gray-500 hover:bg-gray-100 focus:ring-gray-200"
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
    } else {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = ''; // Restore scrolling when modal is closed
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose]);

  // Reset closing state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }



  // Determine width class based on size prop or customWidth
  const widthClass = customWidth ?? sizeClasses[size] ?? "max-w-lg";

  // Dynamic height style
  const heightStyle = customHeight ? { height: customHeight } : {};

  // Content padding
  const contentPaddingClass = contentPadding ?? "p-4 md:p-5";

  // Animation classes (Sonar fix - removed nested ternary)
  let backdropAnimationClass = "";
  let modalAnimationClass = "";

  if (showAnimation) {
    backdropAnimationClass = isClosing ? "animate-fadeOut" : "animate-fadeIn";
    modalAnimationClass = isClosing ? "animate-scaleOut" : "animate-scaleIn";
  }
  const paddingClass = padding ?? "p-4";

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto overflow-x-hidden flex justify-center items-center backdrop-blur-sm bg-black/40 ${backdropAnimationClass}`}
    >
      {closeOnBackdropClick && (
        <button
          type="button"
          className="fixed inset-0 w-full h-full bg-transparent border-0 cursor-default"
          onClick={handleClose}
          aria-label="Close modal"
          tabIndex={-1}
        />
      )}
      <dialog
        open
        className={`relative w-full ${widthClass} ${paddingClass} ${modalAnimationClass} bg-transparent border-0 p-0 m-0 max-w-none`}
        aria-modal="true"
        style={customCss}
      >
        <div
          className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl flex flex-col"
          style={heightStyle}
        >
          {/* Modal header */}
          {(header || !hideCloseButton) && (
            <div className="flex items-center justify-between p-4 md:px-6 py-3 border-b rounded-t">
              {header && <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{header}</h3>}
              {!hideCloseButton && (
                <button
                  type="button"
                  className="text-gray-400 bg-transparent hover:bg-gray-100 hover:text-gray-600 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center transition-colors"
                  onClick={handleClose}
                  aria-label="Close modal"
                >
                  <svg
                    className="w-4 h-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="m1 1 12 12m0-12L1 13"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Modal body */}
          <div
            className={`${contentPaddingClass} space-y-4 flex-1 overflow-x-auto`}
            style={{
              overflowX: "auto",
              maxHeight: "calc(100vh - 50px)",
            }}
          >
            <div className="w-full">{children}</div>
          </div>

          {/* Modal footer */}
          {(onDecline || onAccept) && (
            <div className="flex justify-end items-center gap-2 p-4 md:px-6 py-2 border-t border-gray-200 rounded-b">
              {onDecline && (
                <button
                  type="button"
                  className={`${variantClasses[variant].decline} focus:ring-2 focus:outline-none rounded-lg text-sm font-medium px-5 py-2 transition-colors`}
                  onClick={onDecline}
                >
                  {cancelText}
                </button>
              )}
              {onAccept && (
                <button
                  type="button"
                  className={`${variantClasses[variant].accept} focus:ring-2 focus:outline-none font-medium rounded-lg text-sm px-5 py-2 transition-colors`}
                  onClick={onAccept}
                >
                  {acceptText}
                </button>
              )}
            </div>
          )}
        </div>
      </dialog>
    </div>
  );
};

export default GenericModal;
